
import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { track } from '../lib/analytics';

// Trouve+ est offert gratuitement en early bird : la candidature simplifiée
// par IA est limitée à quelques essais pour permettre de tester la feature
// sans faire exploser les coûts d'appels IA.
export const FREE_REACHOUT_CREDITS = 3;

/**
 * Lit le nombre d'essais de candidature simplifiée déjà utilisés, sans en
 * consommer un. Utilisé pour afficher "X/3 essais restants" dans l'UI.
 */
export const getReachoutUsage = async (userId) => {
    try {
        const { data, error } = await supabase
            .from('profiles')
            .select('cv_data')
            .eq('id', userId)
            .single();

        if (error) throw error;

        const history = data?.cv_data?.ai_generation_history || [];

        return {
            count: history.length,
            remaining: Math.max(0, FREE_REACHOUT_CREDITS - history.length),
            limit: FREE_REACHOUT_CREDITS,
        };
    } catch (err) {
        console.error("Error fetching reachout usage:", err);
        return { count: 0, remaining: FREE_REACHOUT_CREDITS, limit: FREE_REACHOUT_CREDITS };
    }
};

export const useAIReachout = () => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);

    const generateReachout = async (userId, offer) => {
        setIsGenerating(true);
        setError(null);

        try {
            // 1. Fetch user profile (CV and usage history)
            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('cv_url, cv_data')
                .eq('id', userId)
                .single();

            if (profileError) throw new Error("Impossible de récupérer votre profil.");

            if (!profile?.cv_url && !profile?.cv_data) {
                throw new Error("Aucun CV trouvé. Veuillez d'abord uploader un CV dans votre profil.");
            }

            // 1.5 Check free credits (3 essais gratuits pendant l'early bird)
            const cvData = profile.cv_data || {};
            const history = cvData.ai_generation_history || [];

            if (history.length >= FREE_REACHOUT_CREDITS) {
                throw new Error(`Tu as utilisé tes ${FREE_REACHOUT_CREDITS} essais gratuits de candidature simplifiée. Cette fonctionnalité repassera bientôt en illimité !`);
            }

            // 2. Prepare Payload (JSON for Edge Function)
            const payload = {
                cv_url: profile.cv_url || '',
                cv_data: profile.cv_data,
                id_offre: offer.id,
                offre_complete: offer.description || offer.details?.poste || "",
                offer_title: offer.title,
                offer_company: offer.company,
                offer_city: offer.city,
                offer_country: offer.country
            };

            // 3. Call Supabase Edge Function (Proxy)
            const { data, error: funcError } = await supabase.functions.invoke('ai-reachout', {
                body: payload
            });

            if (funcError) {
                console.error("Function Error:", funcError);
                throw new Error("Erreur lors de la communication avec l'IA.");
            }

            // 4. Handle Response
            let resultText = '';
            if (typeof data === 'string') {
                resultText = data;
            } else if (typeof data === 'object' && data !== null) {
                // Try to extract text from common keys, otherwise stringify
                resultText = data.text || data.output || data.message || JSON.stringify(data, null, 2);
            } else {
                resultText = String(data);
            }

            // 5. Update usage count
            const newHistoryEntry = {
                offerId: offer.id,
                timestamp: new Date().toISOString()
            };

            await supabase
                .from('profiles')
                .update({
                    cv_data: {
                        ...cvData,
                        ai_generation_history: [...history, newHistoryEntry]
                    }
                })
                .eq('id', userId);

            track('feature_used', { feature: 'ai_mail', offer_id: offer.id, credits_used: history.length + 1 });
            return resultText;

        } catch (err) {
            console.error("AI Reachout Error:", err);
            setError(err.message);
            return null;
        } finally {
            setIsGenerating(false);
        }
    };

    return {
        generateReachout,
        isGenerating,
        error
    };
};
